import { createI18n } from 'vue-i18n'

import de from './locales/de'
import en from './locales/en'
import hr from './locales/hr'
import hu from './locales/hu'
import it from './locales/it'
import sl from './locales/sl'

export type AvailableLocales = 'sl' | 'en' | 'de' | 'it' | 'hr' | 'hu'

/**
 * The language the site opens in.
 *
 * Slovenian by default and the visitor's own choice after that. Not the browser's
 * Accept-Language: a booking site is read by people who are about to travel, and half
 * of them are looking at it on a phone set to a language they do not want the hotel
 * to talk to them in. The choice is remembered because the language is also what the
 * confirmation letter is written in.
 */
/**
 * Slovenian has four plural forms and vue-i18n ships the English two.
 *
 * one (1, 101), two (2, 102), few (3, 4) and other (everything else, 5 and up) — the
 * CLDR rule for `sl`, counted on the last two digits. Without it a booking site tells
 * a guest "2 sob" and "2 odraslih", which is the kind of mistake a reader takes as a
 * sign the hotel does not run this website itself.
 *
 * A message written with fewer forms than the rule can pick — every English one — is
 * clamped to its last, so the rule is safe to leave on for both languages.
 */
const slovenePlural = (choice: number, choicesLength: number): number => {
    const count = Math.abs(choice) % 100

    let form = 3

    if (count === 1) form = 0
    else if (count === 2) form = 1
    else if (count === 3 || count === 4) form = 2

    return Math.min(form, choicesLength - 1)
}

/**
 * Croatian has three: one (1, 21, 31 — but not 11), few (2–4, 22–24 — but not 12–14)
 * and other (everything else). The CLDR rule for `hr`, counted on the last digit with
 * the teens taken out, and the same rule Serbian and Bosnian follow.
 *
 * Without it vue-i18n's own rule reads a three-part message as zero/one/many and a
 * guest is offered "Još 2 slobodnih soba". Clamped like the Slovenian one, so a
 * Croatian message may be written with fewer forms where the word does not change.
 */
const croatianPlural = (choice: number, choicesLength: number): number => {
    const count = Math.abs(choice)
    const last  = count % 10
    const teens = count % 100

    let form = 2

    if (last === 1 && teens !== 11) form = 0
    else if (last >= 2 && last <= 4 && (teens < 12 || teens > 14)) form = 1

    return Math.min(form, choicesLength - 1)
}

/*
 * German, Italian and English have the two forms vue-i18n picks by default, and
 * Hungarian leaves the noun in the singular after a numeral ("2 éjszaka") — so its
 * messages carry one form and no rule of their own.
 */

const i18n = createI18n({
    legacy:           false,
    globalInjection:  true,
    locale:           localStorage.getItem('locale') || 'sl',
    fallbackLocale:   'en',
    messages:         { sl, en, de, it, hr, hu },
    availableLocales: ['sl', 'en', 'de', 'it', 'hr', 'hu'],
    pluralRules:      { sl: slovenePlural, hr: croatianPlural },
})

export default i18n
